import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import { MxAlertService, MxBaseRestService } from 'mx-commons';
import { Subscription } from 'rxjs';
import { ETypeUser, UserAcceptanceTermDto } from 'src/app/model/user';
import { UserAuthIp } from 'src/app/model/userAuthIp';
import {
  IResponseDownload,
  UploadService,
} from 'src/app/shared/components/upload/upload.service';
import {
  getErrorMessageHttp,
  isNotNullAndUndefined,
  isNullOrUndefined,
} from 'src/app/shared/helpers/utils';
import { GenericValidator } from 'src/app/shared/services/generic-validator.service';
import { LogoService } from 'src/app/shared/services/logo.service';
import { UtilsService } from 'src/app/shared/services/utils.service';
import { environment } from 'src/environments/environment';
import {
  ConfigurationService,
  IAcceptanceTerm,
} from '../configuration/configuration.service';
import { LoginService, UserViewModel } from './login.service';

@Component({
  selector: 'app-login',
  templateUrl: 'login.component.html',
  styleUrls: ['login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  mxApp = '/';
  mxEnvironment = { production: false, baseApi: '/', baseUrl: '/' };

  usuario: any = { userName: null, password: null, keepLogged: false };
  private inscricao: Subscription;
  private redirect = false;
  private forward: string;
  private tokenRecovery: string;
  loggedUser: UserViewModel = null;
  userAuthIp: UserAuthIp = null;
  recoveryPassword = false;
  changePassword = false;
  twoFactorAuth = false; //controla se o painel de autenticação deve aparecer ou nao
  acceptanceTermDisplay = false; //controla se o painel do aceite do termo deve aparecer ou nao
  tempToken: string = null; //guarda o token temporariamente
  isLoading = true;
  isLoadingLogin = false;
  isPasswordSecure: boolean;

  formRecovery: FormGroup;
  formChangePassword: FormGroup;
  formTwoFactorAuth: FormGroup;
  formTwoFactorAuthForResetToken: FormGroup;
  formForceChangePassword: FormGroup;
  forceChangePassword = false;
  messageMatchPassword = { matchOther: () => 'As senhas não coincidem.' };
  public acceptedTerm: boolean = false;
  public acceptanceTerm: IAcceptanceTerm = null;
  resetToken = false;
  buttonResetToken = false;
  logoPreview: any;
  backgroundPreview: any;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly authService: LoginService,
    private readonly baseRest: MxBaseRestService,
    private readonly alertService: MxAlertService,
    private readonly http: HttpClient,
    private readonly translate: TranslateService,
    private readonly formBuilder: FormBuilder,
    private readonly utilsService: UtilsService,
    private readonly uploadService: UploadService,
    private readonly configurationService: ConfigurationService,
    private logoService: LogoService,
    private readonly sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    //caso ocorra timeout e venha com um modal aberto
    let modal = document.querySelector('modal-container');
    if (modal) {
      let backDrop = document.querySelector('bs-modal-backdrop');
      while (modal.lastChild) {
        modal.removeChild(modal.lastChild);
      }
      backDrop.remove();
      modal.remove();
    }

    this.activatedRoute.data.subscribe((param: any) => {
      this.mxEnvironment = param.mxEnvironment
        ? param.mxEnvironment
        : this.mxEnvironment;
    });

    this.inscricao = this.activatedRoute.queryParams.subscribe((param: any) => {
      this.forward =
        (param.forward || '').length > 0
          ? String(param.forward).replace('#/', '')
          : null;
      if (isNotNullAndUndefined(this.forward) && this.forward.startsWith('/')) {
        this.forward = this.forward.substr(1);
      }
      this.redirect = param.redirect;
      this.tokenRecovery = param.token;
      this.changePassword =
        isNotNullAndUndefined(this.tokenRecovery) &&
        this.tokenRecovery.length > 0;
      if (this.changePassword) {
        this.createFormChange();
      }
    });
    this.getLastAcceptanceTerm();
    this.getUserLoggedIn();
    this.getLogo()
    this.getBackground()
  }

  ngOnDestroy(): void {
    if (isNotNullAndUndefined(this.inscricao)) {
      this.inscricao.unsubscribe();
    }
  }

  private async getUserLoggedIn(): Promise<void> {
    let idioma = localStorage.getItem('idioma');
    if (isNullOrUndefined(idioma)) {
      idioma = navigator.language;
      if (isNullOrUndefined(idioma)) {
        idioma = 'pt-br';
      } else if (idioma.indexOf('pt') > -1) {
        idioma = 'pt-br';
      } else {
        idioma = 'en';
      }
      localStorage.setItem('idioma', idioma);
    }
    this.translate.use(idioma);

    if (!isNullOrUndefined(this.redirect) && this.redirect === false) {
      this.isLoading = false;
      return;
    }
    const token = localStorage.getItem('token');
    if (!isNullOrUndefined(token) && token !== 'null') {
      this.isLoading = true;
      try {
        await this.getUserInfo();
      } finally {
        this.isLoading = false;
      }
    } else {
      // this.cleanLocalResources();
      this.isLoading = false;
    }
  }

  private async getUserInfo(validateTerm: boolean = false): Promise<void> {
    try {
      let userInfo = (await this.baseRest
        .getter(`auth/info${validateTerm ? '?validateTerm=true' : ''}`, '', '')
        .toPromise()) as UserViewModel;
      if (userInfo.typeUser != ETypeUser.SYS_USER) {
        let userAuthIpInfo = (await this.baseRest
          .getter(`auth/ip/user`, '', '')
          .toPromise()) as UserAuthIp;
        // Caso usuário nulo (por não encontrar ou termo de aceite desatualizado), mantem o usuário na tela de login
        if ((userInfo.userAcceptanceTerm || this.acceptedTerm || userInfo.typeUser != 1) && userAuthIpInfo.status) {
          // Atualiza registro de usuário sobre o termo de uso aceito
          if (this.acceptanceTerm && this.acceptedTerm) {
            userInfo.userAcceptanceTerm = new UserAcceptanceTermDto(
              this.acceptanceTerm.id,
              new Date()
            );
            userInfo = (await this.baseRest
              .post('users/termAcceptance', '', userInfo)
              .toPromise()) as UserViewModel;
          }
          let accessControlResponse = await this.baseRest
            .getter('access-control', '', '')
            .toPromise();
          const accessControl = accessControlResponse
            ? accessControlResponse
            : 0;
          // Compara se já passou o tempo máximo para troca de senha configurado no access control
          if (
            (userInfo.updatedAt +
              accessControl.timeToChangePassword * 24 * 60 * 60 * 1000 <
            new Date().valueOf() && userInfo.typeUser != 0) || userInfo.alterPassword
          ) {
            this.showForceChangePassword();
            this.isLoadingLogin = false;
          } else {
            this.authService.usuarioAutenticado = true;
            this.redirectAfterLogin(userInfo);
          }
        } else {
          this.acceptanceTermDisplay = true
          // this.cleanLocalResources();
          this.isLoadingLogin = false;
        }
      } else {
        this.authService.usuarioAutenticado = true;
        this.redirectAfterLogin(userInfo);
      }
    } catch (error) {
      this.isLoadingLogin = false;
    }
  }

  redirectAfterLogin(user: UserViewModel): void {
    this.authService.usuario = user;
    this.authService.onUserChange.emit(user);
    if (this.forward && this.forward.length > 0) {
      window.location.href = '#/' + this.forward;
    } else {
      if (this.mxEnvironment.production) {
        window.location.href = '/ma-analisecredito-view/';
      } else {
        window.location.href = '/';
      }
    }
  }

  cleanLocalResources(): void {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
  }

  fazerLogin(): Promise<void> {
    if (
      (this.usuario.userName || '').trim().length === 0 ||
      (this.usuario.password || '').trim().length === 0
    ) {
      this.alertService.warning('Informe o Usuário e Senha!');
      return;
    }

    this.usuario.userName = this.usuario.userName.replace(/\s+/g, '');
    const tokenBasic: string =
      'Basic ' +
      window.btoa(this.usuario.userName + ':' + this.usuario.password);
    
    let httpOptions = null

    if (sessionStorage.getItem('token')) {
      httpOptions = {
        headers: new HttpHeaders({ Authorization: tokenBasic, Token: sessionStorage.getItem('token') }),
        observe: 'response' as 'body',
      };
    } else {
      httpOptions = {
        headers: new HttpHeaders({ Authorization: tokenBasic }),
        observe: 'response' as 'body',
      };
    }

    this.isLoadingLogin = true;

    this.http
      .post<any>(
        `${this.baseRest.baseUrl}${this.baseRest.baseApi}auth/sign-in`,
        null,
        httpOptions
      )
      .subscribe(
        (response) => {
          this.validaLogin(response);
        },
        (error) => {
          this.isLoadingLogin = false;
          // this.cleanLocalResources();
          if (error.error && error.error.status === 401) {
            this.alertService.error('Login/Senha inválidos');
          } else {
            if (error.error == "No token available") {
              this.alertService.error("Não há tokens disponíveis, deslogue de um dispositivo conectado.")
              this.buttonResetToken = true
            } else {
            this.alertService.error(
              error.error.message || error.message || 'Login/Senha inválidos'
            );
            }
          }
        }
      );
  }

  private validaLogin(response: any): void {
		const token = (response.body.token || response.headers.get('Authorization'));
		localStorage.setItem('token', token);
		sessionStorage.setItem('token', token);
    	localStorage.setItem('user', this.usuario.userName);
    	sessionStorage.setItem('user', this.usuario.userName);
		this.getUserFromToken();
	}

  private async getUserFromToken(): Promise<void> {
    let userInfo: UserViewModel = null;
    let userAuthIpInfo: UserAuthIp = null;

    try {
      userInfo = (await this.baseRest
        .getter(`auth/info`, '', '')
        .toPromise()) as UserViewModel;

      await this.baseRest
        .getter(`users/confirmlogin/${userInfo.id}`, '', '')
        .toPromise();
    } catch (err) {
      this.isLoadingLogin = false;
      this.alertService.warning('Erro ao pegar informaçoes do usuário');
    }
    if (userInfo.typeUser != ETypeUser.SYS_USER) {
      try {
        userAuthIpInfo = (await this.baseRest
          .getter(`auth/ip/user`, '', '')
          .toPromise()) as UserAuthIp;
      } catch (err) {
        // this.isLoadingLogin = false;
        // this.sendValidationCode();
        console.log(err)
      }
      //verifica se aceitou o termo no passado
      if (this.acceptanceTerm && this.acceptanceTerm.id) {
        if (
          userInfo.userAcceptanceTerm &&
          userInfo.userAcceptanceTerm.acceptedTermId === this.acceptanceTerm.id
        ) {
          this.checkTwoFactorAuthStatus(userAuthIpInfo, userInfo);
        } else {
          //verifica se marcou a opçao de aceite
          if (!this.acceptanceTerm) {
            if (this.acceptanceTermDisplay) {
              this.isLoadingLogin = false;
              this.alertService.warning('Necessário aceitar o termo');
            } else {
              this.showAcceptanceTermDisplay();
            }
          } else {
            this.checkTwoFactorAuthStatus(userAuthIpInfo, userInfo);
          }
        }
      } else {
        this.redirectAfterLogin(userInfo);
      }
    } else {
      this.redirectAfterLogin(userInfo);
    }
  }

  async checkTwoFactorAuthStatus(
    userAuthIp: UserAuthIp,
    userInfo: UserViewModel
  ): Promise<void> {
    //grava um user e um token
    this.loggedUser = userInfo;

    //se ele ja autenticou pelo email vai para o menu
    if (userAuthIp ? userAuthIp.status : false) {
      this.authService.usuarioAutenticado = true;
      this.getUserInfo();
    } else {
      try {
        (await this.baseRest
          .getter(`auth/ip`, '', '')
          .toPromise()) as UserAuthIp;
        this.isLoadingLogin = false;
        this.showTwoFactorAuth();
        this.alertService.info('Código enviado para o e-mail de cadastro');
      } catch (err) {
        this.alertService.error('Erro ao enviar email');
        this.isLoadingLogin = false;
      }
    }
  }

  async validateCode() {
    try {
      let userAuthIp = (await this.baseRest
        .getter(
          `auth/ip/check-code?code=${
            this.formTwoFactorAuth.get('authCode').value
          }`,
          '',
          ''
        )
        .toPromise()) as UserAuthIp;
      this.twoFactorAuth = false;
      this.getUserInfo();
    } catch (err) {
      this.isLoadingLogin = false;
      // this.cleanLocalResources();
      this.alertService.error('Erro ao verificar código');
    }
    this.isLoadingLogin = false;
  }

  showRecoveryPassword(): void {
    this.recoveryPassword = true;
    this.createFormRecovery();
  }

  private createFormRecovery(): void {
    this.formRecovery = this.formBuilder.group({
      username: [
        null,
        Validators.compose([Validators.required, Validators.maxLength(50)]),
      ],
      email: [
        null,
        Validators.compose([Validators.required, Validators.email]),
      ],
    });
  }

  private createFormChange(): void {
    this.formChangePassword = this.formBuilder.group({
      username: [
        null,
        Validators.compose([Validators.required, Validators.maxLength(50)]),
      ],
      password: [
        null,
        Validators.compose([
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(30),
        ]),
      ],
      passwordRenew: [
        null,
        Validators.compose([
          Validators.required,
          GenericValidator.matchOtherValidator('password'),
        ]),
      ],
    });
  }

  showTwoFactorAuth(): void {
    this.createFormTwoFactorAuth();
    this.isLoadingLogin = false;
    this.twoFactorAuth = true;
  }

  private createFormTwoFactorAuth(): void {
    this.formTwoFactorAuth = this.formBuilder.group({
      authCode: [
        null,
        Validators.compose([Validators.required, Validators.maxLength(50)]),
      ],
    });
  }

  async postRecoveryPassword(): Promise<void> {
    this.isLoadingLogin = true;
    try {
      if (this.formRecovery.invalid) {
        this.alertService.warning(
          'Dados obrigatórios não informados ou inválidos!'
        );
        this.utilsService.validateAllFormFields(this.formRecovery);
        return;
      }
      // this.cleanLocalResources();

      try {
        const { username, email } = this.formRecovery.getRawValue();
        await this.baseRest
          .post(
            'auth',
            `recoveryPassword?username=${username}&email=${email}`,
            null
          )
          .toPromise();
        this.alertService.info(
          'As instruções de recuperação foram encaminhadas no endereço de e-mail do usuário, aguarde.'
        );
      } catch (erro) {
        this.alertService.error(getErrorMessageHttp(erro));
        return;
      }

      this.recoveryPassword = false;
    } finally {
      this.isLoadingLogin = false;
    }
  }

  async postChangePassword(): Promise<void> {
    this.isLoadingLogin = true;
    try {
      if (this.formChangePassword.invalid) {
        this.alertService.warning(
          'Dados obrigatórios não informados ou inválidos!'
        );
        this.utilsService.validateAllFormFields(this.formChangePassword);
        return;
      }

      if (
        isNullOrUndefined(this.tokenRecovery) ||
        this.tokenRecovery.length === 0
      ) {
        this.changePassword = false;
        return;
      }

      try {
        this.cleanLocalResources();
        const { username, password } = this.formChangePassword.getRawValue();
        await this.baseRest
          .put(
            'auth',
            `changePassword?token=${this.tokenRecovery}`,
            { username, password },
            ''
          )
          .toPromise();
        this.alertService.success('Senha alterada com sucesso!');
      } catch (erro) {
        this.alertService.error('Erro ao alterar senha');
        return;
      }

      this.tokenRecovery = null;
      this.changePassword = false;
    } finally {
      this.isLoadingLogin = false;
    }
  }

  private getLastAcceptanceTerm(): void {
    const url = 'acceptanceterm/lastAcceptanceTerm';
    this.configurationService.getLastAcceptanceTerm(url, true).then((res) => {
      if (res) this.acceptanceTerm = res;
    });
  }

  public downloadAcceptanceTerm(): void {
    if (this.acceptanceTerm.attachment.attachmentId) {
      this.uploadService
        .download(
          `files/downloadNoAuth/${this.acceptanceTerm.attachment.attachmentId}`,
          { responseType: 'blob' }
        )
        .subscribe(
          (response: IResponseDownload) => {
            if (response && response.blob)
              saveAs(response.blob, response.fileName);
          },
          (error) => {
            this.alertService.error(
              'Ocorreu um erro ao baixar o termo de uso.'
            );
            console.error(error);
          }
        );
    }
  }

  showAcceptanceTermDisplay(): void {
    this.acceptanceTermDisplay = true;
    this.isLoadingLogin = false;
  }

  showForceChangePassword(): void {
    this.createFormForceChangePassword();
    this.forceChangePassword = true;
  }

  private createFormForceChangePassword(): void {
    this.formForceChangePassword = this.formBuilder.group({
      password: [
        null,
        Validators.compose([
          Validators.required,
          // this.createPasswordStrengthValidator(),
        ]),
      ],
      passwordRenew: [
        null,
        Validators.compose([
          Validators.required,
          GenericValidator.matchOtherValidator('password'),
        ]),
      ],
    });

    this.formForceChangePassword
      .get('password')
      .valueChanges.pipe()
      .subscribe((value: string) => {
        this.isPasswordSecure =
          /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/.test(
            value
          );
      });
  }

  async postForceChangePassword(): Promise<void> {
    this.isLoadingLogin = true;
    try {
      if (this.formForceChangePassword.invalid) {
        this.alertService.warning(
          'Dados obrigatórios não informados ou inválidos!'
        );
        this.utilsService.validateAllFormFields(this.formChangePassword);
        return;
      }

      try {
        const { password } = this.formForceChangePassword.getRawValue();
        await this.baseRest
          .post('auth/forceChangePassword', '', { password })
          .toPromise();
        this.alertService.success('Senha alterada com sucesso!');
        this.forceChangePassword = false;
      } catch (erro) {
        if (erro.error.message) {
          this.alertService.error(erro.error.message);
        } else {
          this.alertService.error('Erro ao salvar senha');
        }
        return;
      }

      this.tokenRecovery = null;
      this.changePassword = false;
    } finally {
      this.isLoadingLogin = false;
    }
  }

  createPasswordStrengthValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const testStrength =
        /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/.test(
          control.value
        );

      return !testStrength ? { passwordStrength: true } : null;
    };
  }

  async sendValidationCode() {
    try {
      (await this.baseRest.getter(`auth/ip`, '', '').toPromise()) as UserAuthIp;
      this.isLoadingLogin = false;
      this.showTwoFactorAuth();
      this.alertService.info('Código enviado para o e-mail de cadastro');
    } catch (err) {
      this.alertService.error('Erro ao enviar email');
      this.isLoadingLogin = false;
    }
  }

  private createFormTwoFactorAuthForResetToken(): void {
    this.formTwoFactorAuthForResetToken = this.formBuilder.group({
      authCode: [
        null,
        Validators.compose([Validators.required, Validators.maxLength(50)]),
      ],
    });
  }

  async resetUserToken() {
    this.buttonResetToken = false

    const tokenBasic: string =
      'Basic ' +
      window.btoa(this.usuario.userName + ':' + this.usuario.password);
    const httpOptions = {
      headers: new HttpHeaders({ Authorization: tokenBasic, ResetToken: "true" }),
      observe: 'response' as 'body',
    };

    const response = await this.http
    .post<any>(
      `${this.baseRest.baseUrl}${this.baseRest.baseApi}auth/sign-in`,
      null,
      httpOptions
    ).toPromise()

    const token = (response.body.token || response.headers.get('Authorization'));
    localStorage.setItem('token', token);
    sessionStorage.setItem('token', token);
    localStorage.setItem('user', this.usuario.userName);
    sessionStorage.setItem('user', this.usuario.userName);

    try {
      (await this.baseRest.getter(`auth/ip/resettoken`, '', '').toPromise()) as UserAuthIp;
      this.isLoadingLogin = false;
      this.alertService.info('Código enviado para o e-mail de cadastro');
    } catch (err) {
      this.alertService.error('Erro ao enviar email');
      this.isLoadingLogin = false;
    }
    this.createFormTwoFactorAuthForResetToken()
    this.resetToken = true
  }

  async validateCodeForResetToken() {
    try {
      let userAuthIp = (await this.baseRest
        .getter(
          `auth/ip/check-code?code=${
            this.formTwoFactorAuthForResetToken.get('authCode').value
          }`,
          '',
          ''
        )
        .toPromise()) as UserAuthIp;

      const isTokenReset = await this.baseRest.getter('tokens/reset-token', '', `?id=${userAuthIp.userId}`).toPromise()

      if (isTokenReset) {
        this.alertService.info('Tokens resetados com sucesso')
      } else {
        this.alertService.warning('Houve um erro interno e não foi possível resetar os tokens')
      }

      this.resetToken = false;
      this.buttonResetToken = false;
      // this.cleanLocalResources();
    } catch (err) {
      this.isLoadingLogin = false;
      this.alertService.error('Erro ao verificar código');
    }
    this.isLoadingLogin = false;
  }

  async getLogo() {
		this.logoService.getLogo("LOGIN").then(res => {
			if (res && res.description != "") {
        this.uploadService.download(`files/downloadNoAuth/${res.attachment.attachmentId}`, { responseType: 'blob' }).subscribe((response: IResponseDownload) => {
          if (response && response.blob) {
            const objectURL = URL.createObjectURL(response.blob);
            this.logoPreview = this.sanitizer.bypassSecurityTrustUrl(objectURL);
          }
        }, error => {
          this.alertService.error("Ocorreu um erro ao baixar o logo.");
          console.error(error);
        });
      }
      else {
        this.logoPreview = `assets/images/${environment.company}login-logo.png`
      }
		}).catch(e => console.log(e));
	}

  async getBackground() {
		this.logoService.getLogo("FUNDO").then(res => {
			if (res && res.description != "") {
        this.uploadService.download(`files/downloadNoAuth/${res.attachment.attachmentId}`, { responseType: 'blob' }).subscribe((response: IResponseDownload) => {
          if (response && response.blob) {
            const objectURL = URL.createObjectURL(response.blob);
            this.backgroundPreview = this.sanitizer.bypassSecurityTrustStyle('url(' + objectURL + ')')
          }
        }, error => {
          this.alertService.error("Ocorreu um erro ao baixar o logo.");
          console.error(error);
        });
      }
      else {
        this.backgroundPreview = 'url(' + `assets/images/${environment.company}login-background.jpg` + ')'
      }
		});
	}
}
